import { FC } from "react";

import { Box } from "@mui/material";
import { TicketingFormCategory } from "@simplyk/common";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";
import { useHasValueChanged } from "@/common/hooks/useHasValueChanged";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { ArrowDown } from "@/icons/outlined";

export type FloatingChooseTicketsButtonProps = {
  show: boolean;
  onClickScrollToTickets: () => void;
};

export const FloatingChooseTicketsButton: FC<FloatingChooseTicketsButtonProps> = ({ show, onClickScrollToTickets }) => {
  const { t } = useTranslate();
  const { isAuction, formCategory } = useFormV2Context();

  const [animateButton, watchedShow] = useHasValueChanged(show);

  const hidden = !watchedShow && !animateButton;

  return (
    <Box
      data-test="floating-choose-tickets-button"
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      padding={hidden ? 0 : 2}
      sx={{
        animation: animateButton
          ? watchedShow
            ? "fade-slide-in 0.3s ease-in-out both"
            : "fade-slide-out 0.3s ease-in-out both"
          : undefined,
        opacity: hidden ? 0 : undefined,
        height: hidden ? 0 : undefined,
        zIndex: 999,
        ["@keyframes fade-slide-in"]: {
          from: { translate: "0 20px", opacity: 0 },
          to: { translate: "0 0", opacity: 1 },
        },
        ["@keyframes fade-slide-out"]: {
          from: { translate: "0 0", opacity: 1 },
          to: { translate: "0 20px", opacity: 0 },
        },
      }}
    >
      <FormButton
        variant="solid"
        endIcon={<ArrowDown />}
        onClick={onClickScrollToTickets}
        data-test="choose-tickets-button"
        sx={{
          width: "100%",
          boxShadow: "0px 16px 32px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        {isAuction
          ? t("ticketing", "viewItems")
          : formCategory === TicketingFormCategory.Shop
            ? t("ticketing", "chooseItems")
            : formCategory === TicketingFormCategory.MembershipV2
              ? t("ticketing", "chooseMembership")
              : isSweepstakesCategory(formCategory)
                ? t("ticketing", "sweepstakes.chooseEntries")
                : t("ticketing", "chooseTickets")}
      </FormButton>
    </Box>
  );
};
